import { useId, useRef, useState } from "react";
import { Calendar } from "./Calendar";
import { Popover } from "./Popover";

interface DateTimeFieldProps {
  // "YYYY-MM-DDTHH:mm", the value a datetime-local input carries.
  value: string;
  onChange: (value: string) => void;
  label: string;
  max?: string;
  required?: boolean;
}

function split(value: string): { day: string; time: string } {
  const [day = "", time = ""] = value.split("T");
  return { day, time: time.slice(0, 5) };
}

function pretty(value: string): string {
  const { day, time } = split(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return "";
  const date = new Intl.DateTimeFormat("en", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    .format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return time ? `${date}, ${time}` : date;
}

// The same calendar as DateField plus the clock, because a journal fact is
// recorded at a moment, not on a day.
export function DateTimeField({ value, onChange, label, max, required = false }: DateTimeFieldProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  const { day, time } = split(value);

  function commit(nextDay: string, nextTime: string) {
    onChange(`${nextDay}T${nextTime || "00:00"}`);
  }

  return (
    <div className="field date-field">
      <span className="field-label" id={`${id}-label`}>{label}</span>
      <button
        type="button" className={`date-trigger ${open ? "open" : ""}`} ref={trigger}
        aria-labelledby={`${id}-label`} aria-haspopup="dialog" aria-expanded={open}
        aria-required={required || undefined} onClick={() => setOpen((value) => !value)}
      >
        <span className={value ? "date-value" : "date-value placeholder"}>{value ? pretty(value) : "Choose a moment"}</span>
        <svg className="date-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3v3M17 3v3M4 9h16M5 6h14v14H5z" />
        </svg>
      </button>
      <Popover
        anchorRef={trigger} open={open} onDismiss={() => setOpen(false)}
        className="calendar-popover" role="dialog" labelledBy={`${id}-label`}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          setOpen(false);
          trigger.current?.focus();
        }}
      >
        <Calendar value={day} max={max?.slice(0, 10)} label={label} onChange={(next) => commit(next, time)} />
        <div className="calendar-time">
          <label className="field-label" htmlFor={`${id}-time`}>Time</label>
          <input id={`${id}-time`} type="time" value={time} onChange={(event) => commit(day, event.target.value)} />
          <button className="primary-button compact" type="button" onClick={() => { setOpen(false); trigger.current?.focus(); }}>
            <span>Done</span><span aria-hidden="true">→</span>
          </button>
        </div>
      </Popover>
    </div>
  );
}
